// Indiana University Bloomington: public college page (#2743), official facts
// only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - IU Bloomington's Common Data Set 2025-2026, which IU publishes as a web
//    application (iuapps.iu.edu/cds, year 2025; its section C reports the
//    Fall 2025 entering class and the test policy for Fall 2027): C1
//    applicants, admits and enrollees with the residency table; C8 test
//    policy for Fall 2027; C21/C22 plans. Its section G marks the 2026-2027
//    costs as not yet final ("not available at this time", due July), so the
//    cost comes from IU's cost of attendance page instead.
//  - IU Bloomington Admissions: Application process for future freshmen,
//    Key freshman application deadlines, Test-optional admissions and
//    Admission paths. The pages carry no cycle year; they are the pages as
//    posted on the read date for students applying in fall 2026.
//  - IU Bloomington Cost of Attendance for Undergraduate Students,
//    "Cost of attendance for full-time, domestic undergraduate students in
//    2026-2027" (IU calls these estimates).
//  - College Scorecard, school id 151351 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'IU Bloomington Common Data Set 2025-2026, Section C',
        url: 'https://iuapps.iu.edu/cds/?p=index&i=home&section=C.+First-Time%2C+%28Freshman%29+Admission&year=2025',
        read: '2026-10-01',
    },
    apply: {
        label: 'IU Bloomington Admissions, Application process for future freshmen',
        url: 'https://bloomington.iu.edu/admissions/apply/freshman/index.html',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'IU Bloomington Admissions, Key freshman application deadlines',
        url: 'https://bloomington.iu.edu/admissions/apply/freshman/deadlines.html',
        read: '2026-10-01',
    },
    tests: {
        label: 'IU Bloomington Admissions, Test-optional admissions',
        url: 'https://bloomington.iu.edu/admissions/apply/freshman/test-optional.html',
        read: '2026-10-01',
    },
    paths: {
        label: 'IU Bloomington Admissions, Admission paths',
        url: 'https://bloomington.iu.edu/admissions/paths/',
        read: '2026-10-01',
    },
    costs: {
        label: 'IU Bloomington, Cost of Attendance for Undergraduate Students',
        url: 'https://bloomington.iu.edu/cost-aid/cost-attendance/undergraduate.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?151351-Indiana-University-Bloomington',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'indiana-university-bloomington',
    kbId: 'indiana_university_bloomington',
    name: 'Indiana University Bloomington',
    shortName: 'IU Bloomington',
    aliases: ['IU', 'IUB'],
    location: 'Bloomington, IN',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (IU brand color guide); decoration only.
    branding: {
        primary: '#990000',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/indiana_university_bloomington_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 74077,
        admitted: 56218,
        enrolled: 10127,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 17458, admitted: 12612 },
            { group: 'Out-of-state', applied: 50700, admitted: 39841 },
            { group: 'International', applied: 5919, admitted: 3765 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'IU Bloomington\'s Common Data Set reports one first-year acceptance rate for the whole campus, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'IU Bloomington has no early decision plan. Its early action plan is not binding and not restrictive; the Common Data Set lists a November 1 closing date and a January 15 notification date, and does not report how many applied or were admitted early.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to IU Bloomington with an intended major. In most of IU\'s 12 schools, naming a major in that school is what gets you directly admitted to it. Some work differently:',
        rows: [
            { school: 'Jacobs School of Music', requirement: 'For any Jacobs major other than Theatre and Drama, an audition and interview are required to be considered for direct admission. You cannot begin at IU as a pre-music major.' },
            { school: 'Kelley School of Business, O\'Neill School of Public and Environmental Affairs, School of Nursing', requirement: 'You may be directly admitted, or follow a standard or traditional admission path, depending on space and academic readiness. Additional deadlines and application steps may apply.' },
            { school: 'Indiana Resident Admissions', requirement: 'Indiana students who earn the Enrollment Honors Plus Seal diploma are offered guaranteed admission.', source: 'apply' },
        ],
        source: 'paths',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular decision', value: 'February 1' },
        rows: [
            { plan: 'Early action', deadline: 'November 1', decision: 'January 15', binding: false },
            { plan: 'Regular decision', deadline: 'February 1', decision: 'March 15', binding: false },
            { plan: 'After February 1', deadline: 'Space-available', decision: 'Case by case', binding: false },
        ],
        note: 'Your complete application, with official transcript, essay and any test scores you choose to send, is due by the deadline. Applying early action gives the highest admission and scholarship consideration. Some scholarships need SAT, ACT or CLT scores by January 15. A response can be an offer, a request for more information, an invitation to transfer or a waitlist invitation. To enroll, declare your intent and pay the $100 deposit by May 1.',
        source: 'deadlines',
        binding: 'Early action is not binding: even if you are accepted, you are not required to attend IU Bloomington.',
        bindingSource: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'IU Bloomington is test-optional: you choose on the application whether your SAT, ACT or CLT scores are considered, and students without scores get full consideration. IU says it prefers that you share any scores you have. It superscores the SAT, ACT and CLT. If you choose not to have scores considered, they are left out even if IU receives them.',
        source: 'tests',
        cdsLine: 'The Common Data Set marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by February 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and mandatory fees', amounts: [12142, 42702] },
            { item: 'Housing and food', amounts: [14398, 14398] },
            { item: 'Books and supplies', amounts: [1320, 1320] },
            { item: 'Transportation', amounts: [284, 2106] },
            { item: 'Personal expenses', amounts: [2430, 2430] },
        ],
        totals: [30574, 62956],
        note: 'For a full-time domestic undergraduate, fall and spring semesters. IU calls these estimated costs.',
        source: 'costs',
        netPrice: {
            average: 16003,
            byIncome: [
                { income: '$0 to $30,000', amount: 6683 },
                { income: '$30,001 to $48,000', amount: 6890 },
                { income: '$48,001 to $75,000', amount: 10091 },
                { income: '$75,001 to $110,000', amount: 18227 },
                { income: 'Over $110,000', amount: 27092 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from IU\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
